import { useState, useEffect, useCallback, useRef, createContext, useContext, type FormEvent, type KeyboardEvent } from 'react';
import { APP_VERSION, CORPUS_STATS, hydrateCorpusStats } from '../constants';
import { fetchCorpusStats } from '../api';

/* ── Landing context — allows the app to return to the landing page ──── */
interface LandingContextValue {
  returnToLanding: () => void;
  pendingQuery: string | null;
  clearPendingQuery: () => void;
}
const LandingContext = createContext<LandingContextValue>({
  returnToLanding: () => {},
  pendingQuery: null,
  clearPendingQuery: () => {},
});
export const useLanding = () => useContext(LandingContext);

/* ── Key config ──────────────────────────────────────────────── */
const LS_API_KEY = 'standards-api-key';
const LS_USER_NAME = 'standards-user-name';
const LS_USER_ROLE = 'standards-user-role';

const API_BASE = import.meta.env.VITE_API_URL || '';

/* ── Helpers ─────────────────────────────────────────────────── */

function getStoredKey(): string | null {
  try { return localStorage.getItem(LS_API_KEY); } catch { return null; }
}
function storeKey(key: string): void {
  try { localStorage.setItem(LS_API_KEY, key); } catch { /* */ }
}
function clearAuth(): void {
  try {
    localStorage.removeItem(LS_API_KEY);
    localStorage.removeItem(LS_USER_NAME);
    localStorage.removeItem(LS_USER_ROLE);
    localStorage.removeItem('standards-session-id');
    localStorage.removeItem('standards-user-id');
    localStorage.removeItem('standards-app-messages');
    localStorage.removeItem('standards-app-active-query-id');
  } catch { /* */ }
}

export function getApiKey(): string | null { return getStoredKey(); }
export function getAuthHeaders(): Record<string, string> {
  const key = getStoredKey();
  return key ? { Authorization: `Bearer ${key}` } : {};
}
export function handleAuthFailure(): void {
  clearAuth();
  window.location.reload();
}

/* ── Static data ─────────────────────────────────────────────── */

interface DomainInfo {
  name: string;
  slug: string;
  color: string;
  questions: string[];
}

const DOMAIN_DATA: DomainInfo[] = [
  {
    name: 'NCC / Building Code',
    slug: 'ncc',
    color: '#6366f1',
    questions: [
      'What is the minimum ceiling height for habitable rooms in Class 1 buildings?',
      'What fire resistance level is required for walls between sole-occupancy units?',
      'What are the NCC 2025 energy efficiency requirements for new dwellings?',
      'What weatherproofing requirements apply to external walls under the NCC?',
      'What are the sound insulation requirements between Class 2 apartments?',
      'What are the minimum stairway dimensions for a Class 2 building?',
      'What are the ventilation requirements for a residential bathroom?',
      'What accessible design features are required in a Class 2 common area?',
    ],
  },
  {
    name: 'Electrical Safety',
    slug: 'electrical',
    color: '#f59e0b',
    questions: [
      'What are the earthing requirements for residential installations under AS/NZS 3000?',
      'When is RCD protection required for power circuits in a kitchen?',
      'How do I determine cable sizing for a 32A circuit?',
      'What are the clearance requirements for residential switchboards?',
      'What is the minimum socket outlet spacing in a kitchen under AS/NZS 3000?',
      'How do I select the correct circuit breaker rating for a lighting circuit?',
      'What are the requirements for lighting circuits in a Class 2 building?',
      'What are the wiring requirements for interconnected smoke alarms?',
    ],
  },
  {
    name: 'Fire Safety',
    slug: 'fire',
    color: '#ef4444',
    questions: [
      'What fire resistance level is required between a garage and habitable rooms?',
      'What are the requirements for fire doors in a Class 2 building?',
      'What fire detection systems are required in residential care buildings?',
      'What are the requirements for fire hydrant coverage in commercial buildings?',
      'What smoke hazard management systems are required for Class 5 buildings?',
      'When are sprinkler systems mandatory under the NCC?',
      'What are the fire separation requirements for a mixed-use podium?',
      'What are the egress requirements for a Class 9 assembly building?',
    ],
  },
  {
    name: 'Plumbing',
    slug: 'plumbing',
    color: '#06b6d4',
    questions: [
      'What is the minimum drainage fall for a 100mm sewer pipe?',
      'When is backflow prevention required under AS/NZS 3500?',
      'What are the hot water tempering valve requirements for bathrooms?',
      'How do I determine pipe sizing for a residential water supply?',
      'What are the venting requirements for a plumbing drainage system?',
      'What is the minimum water pressure required at a fixture?',
      'What are the minimum fixture spacing requirements for a bathroom?',
      'What are the requirements for connecting to a sewer main?',
    ],
  },
  {
    name: 'Disability Access',
    slug: 'access',
    color: '#a855f7',
    questions: [
      'What are the luminous contrast requirements for stair nosings?',
      'Where are tactile ground surface indicators required?',
      'What are the minimum dimensions for an accessible toilet under AS 1428.1?',
      'What is the minimum doorway width for wheelchair access?',
      'What are the maximum ramp gradients under AS 1428.1?',
      'What are the handrail requirements for accessible ramps?',
      'What signage requirements apply to accessible facilities?',
      'When is a hearing augmentation system required in a building?',
    ],
  },
  {
    name: 'Structural',
    slug: 'structural',
    color: '#64748b',
    questions: [
      'What are the wind load requirements for residential structures in Region A?',
      'What imposed floor loads apply to residential buildings under AS 1170.1?',
      'What earthquake design category applies to my site?',
      'What are the footing requirements for reactive clay sites?',
      'What steel connection standards apply under AS 4100?',
      'What are the timber framing requirements under AS 1684?',
      'What snow load requirements apply to alpine regions?',
      'What are the concrete strength requirements for residential slabs?',
    ],
  },
  {
    name: 'Energy',
    slug: 'energy',
    color: '#f97316',
    questions: [
      'What are the minimum energy efficiency ratings for new homes under NCC 2025?',
      'What insulation R-values are required for Climate Zone 6?',
      'What are the NCC glazing provisions for energy efficiency?',
      'When is a whole-of-house energy rating required?',
      'What are the lighting power density limits for commercial buildings?',
      'What HVAC efficiency requirements apply under Section J6?',
      'What are the building sealing requirements for energy efficiency?',
      'What renewable energy provisions are in NCC 2025?',
    ],
  },
  {
    name: 'Glazing',
    slug: 'glazing',
    color: '#22c55e',
    questions: [
      'When is safety glass required near a doorway under AS 1288?',
      'What glazing is required for windows below 800mm from floor level?',
      'What are the requirements for glass balustrades?',
      'When is Grade A safety glass required vs Grade B?',
      'What wind load rating is needed for glazing in cyclonic regions?',
      'What are the glazing requirements for wet areas like bathrooms?',
      'What impact ratings apply to glazing in human traffic areas?',
      'What are the marking requirements for safety glass?',
    ],
  },
  {
    name: 'WaterMark',
    slug: 'watermark',
    color: '#3b82f6',
    questions: [
      'What is the WaterMark certification process for plumbing products?',
      'How do I check if a product is WaterMark listed?',
      'What surveillance testing is required for WaterMark certified products?',
      'Where must the WaterMark be placed on certified products?',
      'What are the WaterMark scheme rules for importers?',
      'What conformity assessment procedures apply to WaterMark products?',
      'Can overseas testing be used for WaterMark certification?',
      'What happens when a WaterMark product is recalled?',
    ],
  },
  {
    name: 'Consumer Law',
    slug: 'consumer',
    color: '#ec4899',
    questions: [
      'What are the statutory warranty periods for residential building work?',
      'What statutory guarantees apply to building products under the ACL?',
      'What are the product recall obligations under Australian Consumer Law?',
      'What constitutes unfair contract terms in building contracts?',
      'What remedies are available for defective building products?',
      'What are a manufacturer\'s liability obligations under the ACL?',
      'What product labelling requirements apply to building materials?',
      'What consumer rights apply when a tradesperson provides faulty work?',
    ],
  },
  {
    name: 'EESS Equipment Safety',
    slug: 'eess',
    color: '#14b8a6',
    questions: [
      'What electrical equipment must be registered on the EESS?',
      'What test reports are required for EESS registration?',
      'What are the RCM marking requirements for electrical products?',
      'What are the supplier obligations for EESS registered equipment?',
      'How long is an EESS certificate valid?',
      'What product categories are covered by the EESS?',
      'When is a declaration of conformity sufficient for EESS?',
      'What are the recall procedures for EESS registered equipment?',
    ],
  },
  {
    name: 'NATA',
    slug: 'nata',
    color: '#14b8a6',
    questions: [
      'What are NATA accreditation requirements for testing laboratories?',
      'What is the NATA assessment process for calibration labs?',
      'How often are NATA surveillance assessments conducted?',
      'What are the NATA requirements for proficiency testing?',
      'What management system requirements does NATA require?',
      'What are NATA\'s technical competence criteria for test labs?',
      'How do I apply for NATA accreditation?',
      'What are the NATA requirements for on-site testing?',
    ],
  },
  {
    name: 'JAS-ANZ',
    slug: 'jasanz',
    color: '#8b5cf6',
    questions: [
      'What is the JAS-ANZ accreditation process for certification bodies?',
      'What are the JAS-ANZ requirements for product certification schemes?',
      'How does JAS-ANZ assess conformity assessment bodies?',
      'What are the JAS-ANZ mutual recognition arrangements?',
      'What surveillance requirements does JAS-ANZ impose on accredited bodies?',
      'What are the JAS-ANZ requirements for management system certification?',
      'How do I check if a certification body is JAS-ANZ accredited?',
      'What are the JAS-ANZ criteria for personnel certification?',
    ],
  },
  {
    name: 'UK / EU',
    slug: 'ukeu',
    color: '#0ea5e9',
    questions: [
      'What are the CE marking requirements for construction products?',
      'How do UKCA and CE marking differ post-Brexit?',
      'What are the UK Building Regulations Part B fire safety requirements?',
      'What CPR requirements apply to construction products in the EU?',
      'What are the EN standards for electrical safety in the EU?',
      'What are the UK requirements for energy performance certificates?',
      'How do EU product safety directives compare to Australian standards?',
      'What are the REACH requirements for construction materials?',
    ],
  },
];

/* FEATURES section removed — landing is now single-viewport hero only */

/* ── Domain Explorer — expandable pills with sample questions ── */
/* Exported for use as empty state on query page */

export { DOMAIN_DATA };
export type { DomainInfo };

export function DomainExplorer({ onQuestionClick }: { onQuestionClick?: (q: string) => void }) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const toggle = useCallback((slug: string) => {
    setExpanded(prev => prev === slug ? null : slug);
  }, []);

  return (
    <div className="landing-domain-explorer">
      <div className="landing-domain-pills-wrap">
        {DOMAIN_DATA.map(d => (
          <div key={d.slug} className="landing-domain-group">
            <button
              className={`landing-domain-pill landing-domain-pill--interactive${expanded === d.slug ? ' landing-domain-pill--active' : ''}`}
              onClick={() => toggle(d.slug)}
              aria-expanded={expanded === d.slug}
            >
              <span className="landing-pill-dot" style={{ background: d.color }} />
              <span className="landing-pill-name">{d.name}</span>
              <span className="landing-pill-chevron">{expanded === d.slug ? '▾' : '▸'}</span>
            </button>
          </div>
        ))}
      </div>
      {/* Expanded questions — rendered outside the pills grid for full-width layout */}
      {expanded && (() => {
        const d = DOMAIN_DATA.find(x => x.slug === expanded);
        if (!d) return null;
        return (
          <div className="landing-domain-questions" key={d.slug}>
            <p className="landing-domain-questions-label">
              <span className="landing-pill-dot" style={{ background: d.color }} />
              Sample questions for <strong>{d.name}</strong>
            </p>
            <div className="landing-domain-questions-grid">
              {d.questions.map((q, i) => (
                <button
                  key={i}
                  className="landing-domain-q"
                  onClick={() => onQuestionClick?.(q)}
                  title={onQuestionClick ? 'Click to ask this question' : undefined}
                >
                  <span className="landing-domain-q-icon">→</span>
                  {q}
                </button>
              ))}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

/* ── Component ───────────────────────────────────────────────── */

interface Props { children: React.ReactNode; }

export default function AuthGate({ children }: Props) {
  const [apiKey, setApiKey] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [loading, setLoading] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [authed, setAuthed] = useState<boolean>(() => !!getStoredKey());
  // Always start on landing page — Grant directive: logged-in users see landing first.
  // F34/F43 exception: ANY deep path (/q/<id>, /tools/*, /research, /portal/*) is
  // explicit intent to open that view — the landing page would swallow it (the
  // route's component never mounts). Landing still shows for plain "/" loads.
  const [enteredHub, setEnteredHub] = useState<boolean>(() => {
    const p = window.location.pathname;
    if (p && p !== '/' && p !== '') return true;
    return false;
  });
  const [pendingQuery, setPendingQuery] = useState<string | null>(null);
  const [userName, setUserName] = useState<string>(() => {
    try { return localStorage.getItem(LS_USER_NAME) || ''; } catch { return ''; }
  });
  const [userRole, setUserRole] = useState<string>(() => {
    try { return localStorage.getItem(LS_USER_ROLE) || ''; } catch { return ''; }
  });
  // F38/F41: live counts for the landing stats bar (build-time CORPUS_STATS as
  // fallback). The landing renders before App boot, so hydrate here too.
  const [liveStats, setLiveStats] = useState<{ clauses?: number; pdfs?: number; pageImages?: number } | null>(null);

  useEffect(() => {
    void fetchCorpusStats().then((stats) => {
      if (stats) {
        setLiveStats(stats);
        hydrateCorpusStats(stats);
      }
    });
  }, []);

  const enterHub = useCallback(() => {
    // F33: stop the Three.js landing viz BEFORE mounting the app — blanking the
    // iframe kills its WebGL loop synchronously so it can't compete with the
    // (heavy) first MainApp render.
    try {
      const viz = document.querySelector<HTMLIFrameElement>('iframe[src*="crosslinks"]');
      if (viz) viz.src = 'about:blank';
    } catch {}
    try { sessionStorage.setItem('ch-entered', 'true'); } catch {}
    setEnteredHub(true);
  }, []);

  const returnToLanding = useCallback(() => {
    try { sessionStorage.removeItem('ch-entered'); } catch {}
    setPendingQuery(null);
    setEnteredHub(false);
  }, []);

  const clearPendingQuery = useCallback(() => {
    setPendingQuery(null);
  }, []);

  /* Handler: clicking a sample question on the landing page */
  const handleQuestionClick = useCallback((q: string) => {
    if (authed) {
      setPendingQuery(q);
      // F33: same viz teardown as enterHub — this path also mounts MainApp
      try {
        const viz = document.querySelector<HTMLIFrameElement>('iframe[src*="crosslinks"]');
        if (viz) viz.src = 'about:blank';
      } catch {}
      try { sessionStorage.setItem('ch-entered', 'true'); } catch {}
      setEnteredHub(true);
    }
  }, [authed]);

  // Always clear hub-entered on mount so landing shows first
  useEffect(() => {
    try { sessionStorage.removeItem('ch-entered'); } catch {}
  }, []);

  // Validate stored key on mount
  useEffect(() => {
    const storedKey = getStoredKey();
    if (!storedKey) return;
    fetch(`${API_BASE}/api/user/me`, {
      headers: { Authorization: `Bearer ${storedKey}` },
    })
      .then(async (res) => {
        if (res.status === 503) {
          const data = await res.json().catch(() => ({})) as { maintenance?: boolean };
          if (data.maintenance) { setMaintenanceMode(true); return; }
        }
        if (!res.ok) { clearAuth(); setAuthed(false); return; }
        const data = await res.json() as { authenticated: boolean; name?: string; role?: string };
        if (!data.authenticated) { clearAuth(); setAuthed(false); }
        else {
          setMaintenanceMode(false);
          if (data.name) { setUserName(data.name); try { localStorage.setItem(LS_USER_NAME, data.name); } catch {} }
          if (data.role) { setUserRole(data.role); try { localStorage.setItem(LS_USER_ROLE, data.role); } catch {} }
        }
      })
      .catch(() => {});
  }, []);



  const attempt = useCallback(async () => {
    const trimmed = apiKey.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_BASE}/api/user/me`, {
        headers: { Authorization: `Bearer ${trimmed}` },
      });
      if (res.status === 503) {
        const mData = await res.json().catch(() => ({})) as { maintenance?: boolean };
        if (mData.maintenance) { setMaintenanceMode(true); setLoading(false); return; }
      }
      if (!res.ok) {
        setError('Invalid access key. Check your key and try again.');
        setShake(true); setTimeout(() => setShake(false), 600);
        setApiKey(''); setLoading(false); return;
      }
      const data = await res.json() as { authenticated: boolean; name?: string; role?: string; user_id?: string };
      if (!data.authenticated) {
        setError('Invalid access key. Check your key and try again.');
        setShake(true); setTimeout(() => setShake(false), 600);
        setApiKey(''); setLoading(false); return;
      }
      storeKey(trimmed);
      if (data.name) { setUserName(data.name); try { localStorage.setItem(LS_USER_NAME, data.name); } catch {} }
      if (data.role) { setUserRole(data.role); try { localStorage.setItem(LS_USER_ROLE, data.role); } catch {} }
      if (data.user_id) { try { localStorage.setItem('standards-user-id', data.user_id); } catch {} }
      setAuthed(true);
      // Don't auto-enter hub — always show landing first (Grant directive)
    } catch {
      setError('Unable to reach the server. Check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [apiKey]);

  const handleSubmit = useCallback((e: FormEvent) => { e.preventDefault(); attempt(); }, [attempt]);
  const handleKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => { if (e.key === 'Enter') attempt(); }, [attempt]);
  const handleSignOut = useCallback(() => {
    clearAuth(); setAuthed(false); setUserName(''); setUserRole(''); setApiKey('');
  }, []);

  /* ── Authenticated + entered: show the app ─────────────────── */
  if (authed && enteredHub) {
    // AppHeader (rendered inside MainApp and PortalLayout) now handles
    // user info, sign out, and tab navigation. No wrapper bar needed here.
    return (
      <LandingContext.Provider value={{ returnToLanding, pendingQuery, clearPendingQuery }}>
        {children}
      </LandingContext.Provider>
    );
  }

  /* ── Maintenance splash — non-admin users see this when MAINTENANCE_MODE is on ── */
  if (maintenanceMode && userRole !== 'admin') {
    return (
      <div className="landing" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '100dvh', background: 'var(--bg-primary)', padding: '24px' }}>
        <div style={{ maxWidth: 480, textAlign: 'center' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🛠️</div>
          <h1 style={{ fontSize: 'clamp(24px, 4vw, 36px)', fontWeight: 700, color: 'var(--ink)', margin: '0 0 12px', letterSpacing: '-0.5px' }}>We're Upgrading</h1>
          <p style={{ fontSize: 16, color: 'var(--ink-3, #94a3b8)', lineHeight: 1.6, margin: '0 0 24px' }}>
            Compliance Hub will return shortly with new features.<br/>
            We appreciate your patience.
          </p>
          <div style={{ width: 48, height: 3, borderRadius: 2, background: 'var(--accent)', margin: '0 auto' }} />
        </div>
      </div>
    );
  }

  /* ── Unauthenticated: minimal sign-in — just "Compliance Hub" + key input ── */
  if (!authed) {
    return (
      <div className="landing" style={{ height: '100dvh', overflow: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#0c0c0e' }}>
        <div style={{ textAlign: 'center', maxWidth: 380, padding: '0 24px', width: '100%' }}>
          <h1 style={{ fontSize: 'clamp(22px, 4vw, 30px)', fontWeight: 700, color: '#f0f0f4', letterSpacing: '-0.5px', marginBottom: 28 }}>Compliance Hub <span style={{ fontSize: '0.5em', fontWeight: 500, color: '#6366f1', letterSpacing: '1px', textTransform: 'uppercase', verticalAlign: 'middle' }}>Beta</span></h1>
          <form onSubmit={handleSubmit} className={`landing-viz-auth ${shake ? 'landing-viz-auth--shake' : ''}`} style={{ flexDirection: 'column', gap: 12 }}>
            <input
              type="password"
              className={`landing-viz-input ${error ? 'landing-viz-input--error' : ''}`}
              placeholder="Access key"
              value={apiKey}
              onChange={(e) => { setApiKey(e.target.value); if (error) setError(''); }}
              onKeyDown={handleKeyDown}
              autoFocus
              autoComplete="off"
              aria-label="Access key"
              style={{ width: '100%' }}
            />
            <button type="submit" className="landing-viz-cta" disabled={!apiKey.trim() || loading} style={{ width: '100%', justifyContent: 'center' }}>
              {loading ? 'Verifying…' : 'Sign In'}
            </button>
          </form>
          {error && <p className="landing-viz-error" role="alert">{error}</p>}
        </div>
      </div>
    );
  }

  /* ── Authenticated landing — fullscreen animated hero ──────────────── */
  return (
    <div className="landing" style={{ height: '100dvh', overflow: 'hidden' }}>
      {userName && (
        <div style={{ position: 'fixed', top: 12, right: 16, zIndex: 50, display: 'flex', alignItems: 'center', gap: 8, fontSize: '13px', color: '#8b8b96' }}>
          <span>{userName}{userRole === 'admin' ? ' · Admin' : ''}</span>
          <button className="landing-viz-link" onClick={handleSignOut} style={{ fontSize: '13px' }}>Sign out</button>
        </div>
      )}
      <div className="landing-viz-hero">
        <div className="landing-viz-wrap">
          <iframe
            src="/crosslinks-g-alpha.html?embed=true"
            allow="autoplay"
            loading="eager"
            title="Standards Crosslinks Visualization"
            style={{ pointerEvents: 'auto' }}
            onLoad={(e) => {
              try {
                const iframeDoc = (e.target as HTMLIFrameElement).contentDocument;
                if (iframeDoc) {
                  iframeDoc.documentElement.style.overflow = 'hidden';
                  iframeDoc.body.style.overflow = 'hidden';
                  iframeDoc.addEventListener('wheel', (we: WheelEvent) => {
                    we.preventDefault();
                    (document.scrollingElement || document.documentElement).scrollBy({ top: we.deltaY, left: we.deltaX, behavior: 'auto' });
                  }, { passive: false });
                  let touchStartY = 0;
                  iframeDoc.addEventListener('touchstart', (te: TouchEvent) => { touchStartY = te.touches[0].clientY; }, { passive: true });
                  iframeDoc.addEventListener('touchmove', (te: TouchEvent) => {
                    const deltaY = touchStartY - te.touches[0].clientY;
                    touchStartY = te.touches[0].clientY;
                    (document.scrollingElement || document.documentElement).scrollBy({ top: deltaY, behavior: 'auto' });
                  }, { passive: true });
                }
              } catch { /* cross-origin */ }
            }}
          />
          <div className="landing-viz-static"><div className="landing-viz-glow" /></div>
        </div>

        <div className="landing-viz-content">
          <h1 className="landing-viz-title">
            Every clause. Every cross-reference.<br/>
            <span className="landing-viz-accent">One search.</span>
          </h1>
          <div className="landing-viz-cta-wrap">
            <button className="landing-viz-cta" onClick={enterHub} autoFocus>Start Querying →</button>
          </div>
        </div>

        <div className="landing-stats-bar landing-stats-bar--hero" style={{ position: 'relative', zIndex: 10 }}>
          <div className="landing-stats-item"><span className="landing-stats-num">{(liveStats?.clauses ?? CORPUS_STATS.clauses).toLocaleString()}</span><span className="landing-stats-label">Clauses</span></div>
          <div className="landing-stats-item"><span className="landing-stats-num">{liveStats?.pdfs ?? CORPUS_STATS.pdfs}</span><span className="landing-stats-label">Source PDFs</span></div>
          <div className="landing-stats-item"><span className="landing-stats-num">{(liveStats?.pageImages ?? CORPUS_STATS.pageImages).toLocaleString()}</span><span className="landing-stats-label">Page Mappings</span></div>
          <div className="landing-stats-item"><span className="landing-stats-num">v{APP_VERSION}</span><span className="landing-stats-label">Live</span></div>
          <div className="landing-stats-item"><span className="landing-stats-num">{CORPUS_STATS.crossRefs.toLocaleString()}</span><span className="landing-stats-label">Cross-References</span></div>
        </div>
      </div>
    </div>
  );
}
